import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "tenant", "owner" ]
  static values = { ownerUsersUrl: String }

  connect() {
    this.abortController = null
  }

  disconnect() {
    this.abortPendingRequest()
  }

  tenantChanged() {
    this.loadOwnerUsers()
  }

  async loadOwnerUsers() {
    const tenantId = this.tenantTarget.value
    const selectedOwnerId = this.ownerTarget.value

    this.abortPendingRequest()

    if (!tenantId) {
      this.replaceOwnerOptions([], null)
      return
    }

    this.setOwnerLoading()
    const requestController = new AbortController()
    this.abortController = requestController

    try {
      const url = new URL(this.ownerUsersUrlValue, window.location.origin)
      url.searchParams.set("tenant_id", tenantId)
      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
        signal: requestController.signal
      })

      if (!response.ok) throw new Error("Unable to load tenant users")

      const payload = await response.json()
      this.replaceOwnerOptions(payload.users || [], selectedOwnerId)
    } catch (error) {
      if (error.name !== "AbortError") this.setOwnerUnavailable()
    } finally {
      if (this.abortController === requestController) this.abortController = null
    }
  }

  replaceOwnerOptions(users, selectedOwnerId) {
    this.ownerTarget.replaceChildren()

    if (users.length === 0) {
      this.appendStatusOption("No active users in this tenant")
      this.ownerTarget.disabled = true
      return
    }

    users.forEach((user) => {
      const option = document.createElement("option")
      option.value = String(user.id)
      option.textContent = user.label
      this.ownerTarget.append(option)
    })

    const preservedOwner = users.some((user) => String(user.id) === selectedOwnerId)
    this.ownerTarget.value = preservedOwner ? selectedOwnerId : String(users[0].id)
    this.ownerTarget.disabled = false
    this.ownerTarget.setCustomValidity("")
    this.ownerTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  setOwnerLoading() {
    this.ownerTarget.replaceChildren()
    this.appendStatusOption("Loading users…")
    this.ownerTarget.disabled = true
    this.ownerTarget.setCustomValidity("")
  }

  setOwnerUnavailable() {
    this.ownerTarget.replaceChildren()
    this.appendStatusOption("Unable to load users")
    this.ownerTarget.disabled = true
    this.ownerTarget.setCustomValidity("Unable to load users for the selected tenant.")
  }

  appendStatusOption(label) {
    const option = document.createElement("option")
    option.textContent = label
    option.selected = true
    option.disabled = true
    this.ownerTarget.append(option)
  }

  abortPendingRequest() {
    this.abortController?.abort()
    this.abortController = null
  }
}
